import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, DollarSign, TrendingDown, TrendingUp, Trash2, Receipt } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/use-currency";
import { useRealtimeSubscription } from "@/hooks/use-realtime";

const Expenses = () => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [newExpense, setNewExpense] = useState({
    category: "marketing",
    description: "",
    amount: "",
    payment_method: "credit_card",
    expense_date: format(new Date(), "yyyy-MM-dd"),
    notes: ""
  });
  const queryClient = useQueryClient();
  const { currency, formatAmount, convertAmount } = useCurrency();

  // Enable realtime updates
  useRealtimeSubscription("business_expenses", "business-expenses");
  useRealtimeSubscription("subscriptions", "total-revenue");

  const categories = [
    { value: "marketing", label: "Marketing" },
    { value: "software", label: "Software & Tools" },
    { value: "office", label: "Office Supplies" },
    { value: "travel", label: "Travel" },
    { value: "utilities", label: "Utilities" },
    { value: "salaries", label: "Salaries" },
    { value: "equipment", label: "Equipment" },
    { value: "other", label: "Other" }
  ];

  const { data: expenses, isLoading } = useQuery({
    queryKey: ["business-expenses"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("business_expenses")
        .select("*")
        .order("expense_date", { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const { data: revenue } = useQuery({
    queryKey: ["total-revenue"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("subscriptions")
        .select("price");

      if (error) throw error;
      return data.reduce((sum, sub) => sum + Number(sub.price), 0);
    }
  });

  const createMutation = useMutation({
    mutationFn: async (expense: typeof newExpense) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("business_expenses")
        .insert({
          user_id: user.id,
          category: expense.category,
          description: expense.description,
          amount: parseFloat(expense.amount),
          currency: currency,
          expense_date: expense.expense_date,
          payment_method: expense.payment_method,
          notes: expense.notes
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-expenses"] });
      toast.success("Expense added successfully");
      setIsAddOpen(false);
      setNewExpense({
        category: "marketing",
        description: "",
        amount: "",
        payment_method: "credit_card",
        expense_date: format(new Date(), "yyyy-MM-dd"),
        notes: ""
      });
    },
    onError: () => {
      toast.error("Failed to add expense");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("business_expenses")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-expenses"] });
      toast.success("Expense deleted");
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newExpense.description || !newExpense.amount) return;
    createMutation.mutate(newExpense);
  };

  const filteredExpenses = expenses?.filter(exp => 
    categoryFilter === "all" || exp.category === categoryFilter
  );

  // Convert all expenses to display currency before summing (default to USD if currency not set)
  const totalExpenses = expenses?.reduce((sum, exp) => {
    const sourceCurrency = (exp.currency as any) || "USD";
    const converted = convertAmount(Number(exp.amount), sourceCurrency, currency);
    return sum + converted;
  }, 0) || 0;
  
  const profit = (revenue || 0) - totalExpenses;

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      marketing: "bg-purple-500/20 text-purple-700 dark:text-purple-300",
      software: "bg-blue-500/20 text-blue-700 dark:text-blue-300",
      office: "bg-green-500/20 text-green-700 dark:text-green-300",
      travel: "bg-orange-500/20 text-orange-700 dark:text-orange-300",
      utilities: "bg-yellow-500/20 text-yellow-700 dark:text-yellow-300",
      salaries: "bg-red-500/20 text-red-700 dark:text-red-300",
      equipment: "bg-indigo-500/20 text-indigo-700 dark:text-indigo-300",
      other: "bg-gray-500/20 text-gray-700 dark:text-gray-300"
    };
    return colors[category] || colors.other;
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading expenses...</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold text-gradient mb-2">Business Expenses</h1>
          <p className="text-muted-foreground">
            Track and manage your business expenses and profitability
          </p>
        </div>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Expense</DialogTitle>
              <DialogDescription>
                Record a new business expense with category and payment details
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={newExpense.category} onValueChange={(value) => setNewExpense({ ...newExpense, category: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Expense description"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Amount ({currency})</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={newExpense.payment_method} onValueChange={(value) => setNewExpense({ ...newExpense, payment_method: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="debit_card">Debit Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newExpense.expense_date}
                  onChange={(e) => setNewExpense({ ...newExpense, expense_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={newExpense.notes}
                  onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
                  rows={2}
                />
              </div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Adding..." : "Add Expense"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-card p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold">{formatAmount(revenue || 0)}</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-500/20 rounded-lg">
              <TrendingDown className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Expenses</p>
              <p className="text-2xl font-bold">{formatAmount(totalExpenses)}</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card p-6">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${profit >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              <DollarSign className={`h-6 w-6 ${profit >= 0 ? 'text-green-500' : 'text-red-500'}`} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Net Profit</p>
              <p className="text-2xl font-bold">{formatAmount(profit)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="glass-card p-4">
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Expenses List */}
      <div className="space-y-3">
        {filteredExpenses && filteredExpenses.length > 0 ? (
          filteredExpenses.map((expense) => (
            <Card key={expense.id} className="glass-card p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className="p-2 bg-muted/50 rounded-lg">
                    <Receipt className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium">{expense.description}</h4>
                      <Badge className={getCategoryColor(expense.category)}>
                        {expense.category}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{format(new Date(expense.expense_date), "MMM dd, yyyy")}</span>
                      {expense.payment_method && (
                        <span className="capitalize">{expense.payment_method.replace("_", " ")}</span>
                      )}
                    </div>
                    {expense.notes && (
                      <p className="text-sm text-muted-foreground mt-1">{expense.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xl font-bold">{formatAmount(Number(expense.amount), (expense.currency || "USD") as any)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMutation.mutate(expense.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="glass-card p-8 text-center">
            <p className="text-muted-foreground">No expenses recorded yet. Add your first expense to start tracking.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Expenses;